/**
 * @file: useFieldErrorFocusController.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { isNotNull } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useMemo, useRef } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Subject, debounceTime, firstValueFrom, race, timer } from 'rxjs';
import { FieldErrorAnchorKey } from './anchorKeys';
import { FieldErrorAnchor, FieldErrorFocusContextProps } from './context';
import {
  MeasurableView,
  WindowRect,
  measureInWindowAsync,
} from './measureInWindowAsync';

/** the scroll is settled once it has been this long without a scroll event */
const kScrollIdleDuration = 100;

/** never hold the shake back longer than this, however the scroll behaves */
const kScrollSettleTimeout = 1500;

type MeasuredAnchor = { anchor: FieldErrorAnchor; rect: WindowRect };

/**
 * owns the form scroll view and the rows registered as error anchors, so that
 * a failed submit can bring the offending row into view and shake it.
 *
 * mount `scrollViewRef` / `onScroll` on the form scroll view and `contentRef`
 * on the view wrapping its contents, then publish `context` to the rows.
 */
export const useFieldErrorFocusController = () => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();

  // the scroll view ref is the native instance with the scrolling methods
  // mixed in, so it can be measured just like a plain view.
  const scrollViewRef = useRef<ScrollView & MeasurableView>(null);
  const contentRef = useRef<View>(null);
  const anchors = useRef(
    new Map<FieldErrorAnchorKey, FieldErrorAnchor>(),
  ).current;

  const scrollOffset$ = useCreation(() => new Subject<number>(), []);

  const onScroll = usePersistFn(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffset$.next(e.nativeEvent.contentOffset.y);
    },
  );

  /**
   * the `scrollTo` animation has no completion callback, and its duration
   * grows with the distance on web — so wait for the scroll events to go
   * quiet instead of guessing how long the row takes to arrive.
   */
  const waitForScrollToSettle = usePersistFn(async () => {
    await firstValueFrom(
      race(
        scrollOffset$.pipe(debounceTime(kScrollIdleDuration)),
        timer(kScrollSettleTimeout),
      ),
    );
  });

  const registerAnchor = usePersistFn(
    (key: FieldErrorAnchorKey, anchor: FieldErrorAnchor) => {
      anchors.set(key, anchor);
      return () => {
        if (anchors.get(key) === anchor) {
          anchors.delete(key);
        }
      };
    },
  );

  const focusFirstError = usePersistFn(async (keys: FieldErrorAnchorKey[]) => {
    const targets = keys.map(key => anchors.get(key)).filter(isNotNull);
    if (!targets.length) return false;

    const [content, viewport, ...rects] = await Promise.all([
      measureInWindowAsync(contentRef.current),
      measureInWindowAsync(scrollViewRef.current),
      ...targets.map(target => target.measure()),
    ]);

    // the form fields are not laid out in the order they're declared in, so
    // the topmost one on screen is the first one the user should be taken to.
    const target = rects
      .map((rect, index) =>
        rect != null ? { anchor: targets[index], rect } : null,
      )
      .filter(isNotNull)
      .reduce<MeasuredAnchor | undefined>(
        (topmost, item) =>
          topmost == null || item.rect.y < topmost.rect.y ? item : topmost,
        undefined,
      );
    if (target == null) return false;

    const topInset = rpx(20);
    const bottomInset = insets.bottom + rpx(98); // the bottom action bar
    const shouldScroll =
      content != null &&
      viewport != null &&
      (target.rect.y < viewport.y + topInset ||
        target.rect.y + target.rect.height >
          viewport.y + viewport.height - bottomInset);

    if (shouldScroll) {
      // park the row in the middle of the area the bottom action leaves free,
      // so the user sees it in context rather than pinned under the header.
      const freeHeight = viewport.height - topInset - bottomInset;
      const rowTopInViewport =
        topInset + Math.max(0, (freeHeight - target.rect.height) / 2);
      scrollViewRef.current?.scrollTo({
        y: Math.max(0, target.rect.y - content.y - rowTopInViewport),
        animated: true,
      });
      // shake it only once it has come to rest in the viewport, or the
      // effect is over before the user ever sees the row.
      await waitForScrollToSettle();
    }

    target.anchor.shake();

    return true;
  });

  const context = useMemo<FieldErrorFocusContextProps>(
    () => ({ registerAnchor, focusFirstError }),
    [focusFirstError, registerAnchor],
  );

  return { scrollViewRef, contentRef, onScroll, context };
};
